import { DirectoryGroup, GroupBusinessSettings } from '../../../../types';
import { Box } from '@mui/system';
import { Controller, useForm } from 'react-hook-form';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  indexGroup,
  saveGroupSettings,
  saveGroupSettingsUserPortal,
  getDirectoryGroupQuery,
} from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';

import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { Messages } from '../../../utils/message-constants';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import SharedButton from '../../../shared/buttons/shared-button';
import SharedIconButton from '../../../shared/buttons/shared-icon-button';
import MenuItem from '@mui/material/MenuItem';
interface GroupGeneralInfoFormProps {
  group: DirectoryGroup;
  groupEmail: string;
  domain: string;
  domains: string[];
  groupSettings: GroupBusinessSettings;
  directoryRoles: string[];
  changeLogRoles: string[];
  primaryDomain: string;
  userView: boolean;
  isUserManager: boolean;
  isUserOwner: boolean;
}

interface FormInput {
  groupName: string;
  groupEmail: string;
  groupDomain: string;
  groupDescription: string;
}

export default function GroupGeneralInfoForm({
  group,
  groupEmail,
  domain,
  domains,
  directoryRoles,
  groupSettings,
  changeLogRoles,
  primaryDomain,
  userView,
  isUserManager,
  isUserOwner,
}: GroupGeneralInfoFormProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    getValues,
    reset,
  } = useForm<FormInput>({
    values: {
      groupName: group.name || '',
      groupEmail: group.email.split('@')[0] || '',
      groupDomain: domain,
      groupDescription: group.description || '',
    },
  });

  const onSubmit = (data: FormInput) => {
    if (
      !userView &&
      isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.EditGeneralInfoSection.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else if (userView) {
      const newSettings: GroupBusinessSettings = {
        ...groupSettings,
        name: data.groupName,
        email: data.groupEmail + `@${data.groupDomain}`,
        description: data.groupDescription,
      };
      userPortalMutate(newSettings);
    } else {
      const newSettings: GroupBusinessSettings = {
        ...groupSettings,
        name: data.groupName,
        email: data.groupEmail + `@${data.groupDomain}`,
        description: data.groupDescription,
      };
      mutate(newSettings);
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (data: GroupBusinessSettings) =>
      saveGroupSettings(group.email, data, groupSettings, changeLogMessage),
    onSuccess: (data) => {
      const updatedGroup = {
        ...group,
        name: data.name,
        email: data.email,
        description: data.description,
      };
      queryClient.setQueryData([UserKeys.GroupSettings, groupEmail], data);
      indexMutate(updatedGroup);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating('group information'),
      });
    },
  });

  const { mutate: userPortalMutate, isPending: userPortalPending } =
    useMutation({
      mutationFn: (data: GroupBusinessSettings) =>
        saveGroupSettingsUserPortal(group.email, data, groupSettings),
      onSuccess: (data) => {
        const updatedGroup = {
          ...group,
          name: data.name,
          email: data.email,
          description: data.description,
        };
        queryClient.setQueryData([UserKeys.GroupSettings, groupEmail], data);
        indexMutate(updatedGroup);
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage:
            Messages.Errors.Network.Updating('group information'),
        });
      },
    });

  const { mutate: indexMutate, isPending: indexLoading } = useMutation({
    mutationFn: (data: DirectoryGroup) =>
      indexGroup(primaryDomain, groupEmail, data),
    onSuccess: (_returnData, data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Group information'),
      });
      queryClient.setQueryData([UserKeys.DirectoryGroup, groupEmail], data);
      queryClient.invalidateQueries({ queryKey: ['Groups'] });
      setChangeLogMessage('');
      reset({
        groupName: data.name,
        groupEmail: data.email.split('@')[0],
        groupDomain: data.email.split('@')[1],
        groupDescription: data.description,
      });

      // The route is keyed by the group's email, so once the domain (and
      // therefore the primary email) has changed, update the URL to match -
      // otherwise a refresh or deep link to the old address will 404.
      if (data.email !== groupEmail) {
        queryClient.setQueryData(
          [UserKeys.DirectoryGroup, data.email, userView],
          data
        );
        navigate(location.pathname.replace(groupEmail, data.email), {
          replace: true,
        });
      }
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating('group information'),
      });
    },
    onSettled: () => {
      if (userView) {
        createEvent(
          analytics.userView.group_details.update_group_general_info,
          {
            module: ModuleList.modules.userView.name,
            section: ModuleList.modules.userView.sections.groupDetails,
          }
        );
      } else {
        createEvent(
          analytics.directory.group_details.update_group_general_info,
          {
            module: ModuleList.modules.directory.name,
            section: ModuleList.modules.directory.sections.groups,
          }
        );
      }
      setOpenChangeLogDialog(false);
    },
  });

  const userPortalCanEditExceptEmail = isUserManager || isUserOwner;

  const domainOptions = domains.includes(domain)
    ? domains
    : [domain, ...domains];

  return (
    <Box
      component="form"
      sx={{
        position: 'relative',
        height: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ position: 'absolute', top: -60, right: 0 }}
      >
        <SharedIconButton
          variant="refresh"
          tooltipLabel="Refresh"
          onClick={() => {
            void queryClient.invalidateQueries({
              queryKey: getDirectoryGroupQuery(groupEmail).queryKey,
            });
          }}
        />
        <SharedButton
          variant="primary"
          disabled={!isDirty || isPending || indexLoading || userPortalPending}
          loading={isPending || indexLoading || userPortalPending}
          type="submit"
        >
          Save
        </SharedButton>
      </Stack>
      <Stack
        sx={{
          flex: 1,
          height: '100%',
          overflow: 'auto',
          margin: '8px 0',
          paddingTop: 2,
        }}
      >
        <Stack gap={1}>
          <Controller
            name={'groupName'}
            control={control}
            rules={{
              required: 'Name is required.',
            }}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'groupName',
                }}
                InputLabelProps={{
                  htmlFor: 'groupName',
                }}
                label="Group Name"
                sx={{ width: 400 }}
                disabled={
                  (!userView &&
                    !isAbleToSeeUIElement(
                      directoryRoles,
                      DirectoryRoles.EditGeneralInfoSection.value
                    )) ||
                  (userView && !userPortalCanEditExceptEmail)
                }
                autoComplete="off"
                error={!!errors.groupName}
                helperText={errors?.groupName?.message || ' '}
                {...field}
              />
            )}
          />
          <Stack flexDirection="row" alignItems="baseline" width="100%" gap={1}>
            <Controller
              name={'groupEmail'}
              control={control}
              rules={{
                required: 'Email is required.',
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  inputProps={{
                    id: 'groupEmail',
                  }}
                  InputLabelProps={{
                    htmlFor: 'groupEmail',
                  }}
                  disabled={
                    (!userView &&
                      !isAbleToSeeUIElement(
                        directoryRoles,
                        DirectoryRoles.EditGeneralInfoSection.value
                      )) ||
                    (userView && !isUserOwner)
                  }
                  label="Group Email Address"
                  sx={{ width: 400 }}
                  autoComplete="off"
                  error={!!errors.groupEmail}
                  helperText={errors?.groupEmail?.message || ' '}
                />
              )}
            />
            <Typography>@</Typography>
            <Controller
              name={'groupDomain'}
              control={control}
              rules={{
                required: 'Domain is required.',
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  disabled={
                    (!userView &&
                      !isAbleToSeeUIElement(
                        directoryRoles,
                        DirectoryRoles.EditGeneralInfoSection.value
                      )) ||
                    (userView && !isUserOwner)
                  }
                  label="Domain"
                  sx={{ width: 250 }}
                >
                  {domainOptions.map((domainOption) => (
                    <MenuItem value={domainOption} key={domainOption}>
                      {domainOption}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>
          <Controller
            name={'groupDescription'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'groupDescription',
                }}
                InputLabelProps={{
                  htmlFor: 'groupDescription',
                }}
                disabled={
                  (!userView &&
                    !isAbleToSeeUIElement(
                      directoryRoles,
                      DirectoryRoles.EditGeneralInfoSection.value
                    )) ||
                  (userView && !userPortalCanEditExceptEmail)
                }
                label="Group Description"
                autoComplete="off"
                multiline
                sx={{ width: 400 }}
              />
            )}
          />
        </Stack>
      </Stack>
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          const newSettings: GroupBusinessSettings = {
            ...groupSettings,
            name: getValues('groupName'),
            email: getValues('groupEmail') + `@${getValues('groupDomain')}`,
            description: getValues('groupDescription'),
          };
          mutate(newSettings);
        }}
        mutateLoading={isPending || indexLoading}
      />
    </Box>
  );
}
